import { Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import type { Role } from '@/types/roles';

/**
 * A guard that WAITS rather than guessing.
 *
 * The stored session takes a tick to read, and during that tick "no user" and
 * "not signed in" look identical. Redirecting then throws a user out of the
 * page they have every right to see, on every refresh — which also makes the
 * page impossible to link to.
 *
 * Being an admin is not a superset of every role: an admin is not a candidate,
 * and admin screens are listed explicitly wherever they apply. `allow` is the
 * whole rule, with no implicit escalation.
 *
 * ANY of the person's roles opens the door. Someone who is both a recruiter and
 * a recommender reaches both trees, and which of their roles happens to be
 * first in the list decides nothing.
 */
export function RequireRole({ allow, children }: { allow: Role[]; children: ReactNode }) {
  const { user, roles, restoring } = useAuth();
  if (restoring) return <div data-testid="auth-restoring" />;
  if (!user) return <Navigate to="/" replace />;
  if (!roles.some((r) => allow.includes(r))) return <Navigate to="/unauthorized" replace />;
  return <>{children}</>;
}
